"use client";

import { Dispatch, SetStateAction, useContext, useState } from "react";
import { UserContext } from "@/contexts/user-context";
import { ResourceContext } from "@/contexts/resource-context";
import { clientDel, clientPost } from "@/service/client-api/client-api";
import { logEvent } from "@/service/logs/log";
import { cn } from "@/lib/utils";
import { ThumbsDownIcon, ThumbsUpIcon } from "lucide-react";

export const ResourceRating = ({
  rating,
  resourceType,
  resourceId,
  onRate = () => {},
}: {
  rating: RatingVM;
  resourceType: "article" | "video" | "tutorial" | "fast_question";
  resourceId: number;
  onRate?: Dispatch<SetStateAction<boolean>>;
}) => {
  const [percent, setPercent] = useState(rating?.percent);
  const [yourVote, setYourVote] = useState(rating?.yourVote);

  const { isLoggedIn } = useContext(UserContext);
  const { access } = useContext(ResourceContext) as ResourceInfo;
  const { logView } = useContext(ResourceContext);

  async function addRating(vote: "up" | "down") {
    const responseJson = await clientPost("rating", {
      resourceType,
      resourceId,
      type: vote,
    });
    setPercent(responseJson.percent);

    logEvent("rating", logView, {
      resourceType,
      resourceId,
      rating_type: vote,
    });

    onRate(true);
  }

  async function deleteRating() {
    const responseJson = await clientDel("rating.remove", {
      resourceType,
      resourceId,
    });
    setPercent(responseJson.percent);

    logEvent("rating", logView, {
      resourceType,
      resourceId,
      rating_type: null,
    });

    onRate(true);
  }

  async function handleVote(vote: "up" | "down") {
    if (!isLoggedIn || !access?.granted) {
      return;
    }
    const newVote = vote === yourVote ? null : vote;
    setYourVote(newVote);

    if (newVote) {
      await addRating(newVote);
    } else {
      await deleteRating();
    }
  }

  return (
    <div
      className={
        "border-base-input flex h-10 w-[144px] rounded-lg border-[1px] text-sm font-medium transition-all hover:scale-105 active:scale-95"
      }
    >
      <div
        className={cn(
          "flex w-[94px] items-center gap-2 px-4 py-2 hover:cursor-pointer",
          yourVote === "up" && "text-remedium-blue",
        )}
        onClick={() => {
          handleVote("up");
        }}
      >
        <ThumbsUpIcon
          size={"16px"}
          className={cn(yourVote === "up" && "stroke-remedium-blue")}
        />
        {percent}%
      </div>
      <div className={"border-base-input h-10 w-0 border-r-[1px]"}></div>
      <div
        className={cn(
          "flex items-center gap-2 px-4 py-2 hover:cursor-pointer",
          yourVote === "down" && "text-remedium-blue",
        )}
        onClick={() => {
          handleVote("down");
        }}
      >
        <ThumbsDownIcon
          size={"16px"}
          className={cn(yourVote === "down" && "stroke-remedium-blue")}
        />
      </div>
    </div>
  );
};
